import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { shareStory } from "../../apis/user-apis/CampaignIssuesApis";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
};

const NAME_REGEX = /^[\p{L}\p{M}\s'-’]+$/u;
const filterName = (value) => value.replace(/[^\p{L}\p{M}\s'-’]/gu, "");

const CampaignIssues = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({ name: "", email: "", message: "" });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);

  const validate = () => {
    let tempErrors = { name: "", email: "", message: "" };
    let isValid = true;
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) {
      tempErrors.name = "Name is required";
      isValid = false;
    } else if (nameTrimmed.split(/\s+/).filter(Boolean).length < 2) {
      tempErrors.name = "Please enter full name";
      isValid = false;
    } else if (!NAME_REGEX.test(nameTrimmed)) {
      tempErrors.name = "Invalid name";
      isValid = false;
    }
    if (!formData.email.trim()) {
      tempErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      tempErrors.email = "Invalid email address";
      isValid = false;
    }
    const storyTrimmed = formData.message.trim();
    if (!storyTrimmed) {
      tempErrors.message = "Please share your story";
      isValid = false;
    } else if (storyTrimmed.length < 20) {
      tempErrors.message = `Story must be at least 20 characters (${storyTrimmed.length}/20)`;
      isValid = false;
    }
    setErrors(tempErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "name" ? filterName(value) : value;

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    setMessage("");
    if (!validate()) return;
    try {
      setLoading(true);
      const response = await shareStory(formData);
      setMessage(response.message);
      setMessageType("success");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setMessage(error?.response?.data?.message || "Something went wrong");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    mb: 1,
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      backgroundColor: "rgba(0,0,0,0.2)",
      "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
      "&:hover fieldset": { borderColor: "#C59324" },
      "&.Mui-focused fieldset": { borderColor: "#C59324" },
    },
    "& input::placeholder": { color: "#fff", opacity: 0.6 },
  };

  return (
    <Box
      sx={{
        backgroundColor: "#0A192F",
        display: "flex",
        alignItems: "center",
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 6, md: 10 },
          }}
        >
          {/* ── Left Content ─────────────────────────────────── */}
          <Box sx={{ flex: 1.2 }}>
            <Typography
              sx={{
                color: "#C59324",
                fontWeight: 700,
                letterSpacing: "0.2em",
                fontSize: "0.85rem",
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              Families Like Ours
            </Typography>

            <Typography
              sx={{
                fontFamily: "'Playfair Display', serif",
                color: "#fff",
                fontWeight: 600,
                lineHeight: 1.1,
                mb: 4,
                fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3.5rem" },
              }}
            >
              Special Needs Families{" "}
              <Box
                component="span"
                sx={{
                  color: "#C59324",
                  fontStyle: "italic",
                  fontWeight: 500,
                }}
              >
                Deserve Better
              </Box>
            </Typography>

            <Typography
              sx={{
                color: "#B8C0D1",
                fontSize: "1.1rem",
                lineHeight: 1.8,
                mb: 3,
                maxWidth: "600px",
              }}
            >
              Our daughter, Jordan, has Down syndrome. Michelle and I have
              navigated IEPs in the Appoquinimink School District for years. We
              know the system's strengths and its weaknesses. I hope to help
              support other families in similar situations navigate the IEP/504
              system.
            </Typography>

            <Typography
              sx={{
                color: "#B8C0D1",
                fontSize: "1.1rem",
                lineHeight: 1.8,
                mb: 5,
                maxWidth: "600px",
              }}
            >
              My wife and I have lived this experience from the inside. This
              experience helps shape my views on policy, funding, resources, and
              accountability, through the eyes of a parent, not a legislator.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Button
                variant="contained"
                onClick={() => scrollTo("#volunteer")}
                sx={{
                  backgroundColor: "#C59324",
                  color: "#000",
                  px: 4,
                  py: 1.8,
                  fontWeight: 700,
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "#A67C1E" },
                }}
              >
                Connect With Gary
              </Button>

              <Button
                variant="outlined"
                onClick={() => scrollTo("#petition")}
                sx={{
                  borderColor: "#C59324",
                  color: "#C59324",
                  px: 4,
                  py: 1.8,
                  fontWeight: 700,
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    borderColor: "#C59324",
                    backgroundColor: "rgba(197,147,36,0.05)",
                  },
                }}
              >
                Sign the School Accountability Petition
              </Button>
            </Box>
          </Box>

          {/* ── Right Form Card ──────────────────────────────── */}
          <Box sx={{ flex: 0.8, width: "100%" }}>
            <Box
              sx={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                p: { xs: 2.5, sm: 3.5, md: 5 },
                maxWidth: "450px",
                mx: { xs: "auto", md: 0 },
                ml: { md: "auto" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  color: "#C59324",
                  fontSize: "4rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  mb: 1,
                }}
              >
                1{" "}
                <Box component="span" sx={{ fontSize: "2rem", color: "#fff" }}>
                  in
                </Box>{" "}
                4
              </Typography>

              <Typography
                sx={{
                  color: "#B8C0D1",
                  mb: 4,
                  fontSize: "0.95rem",
                  letterSpacing: "1px",
                }}
              >
                U.S. adults live with a disability...
              </Typography>

              <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.1)", pt: 4 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    mb: 3,
                    lineHeight: 1.6,
                    opacity: 0.8,
                  }}
                >
                  Are you a family navigating special education in Middletown? I
                  want to hear from you.
                </Typography>

                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Full Name*"
                  variant="outlined"
                  sx={inputSx}
                />
                {errors.name && (
                  <FormHelperText sx={{ color: "#ff6b6b", mb: 2 }}>
                    {errors.name}
                  </FormHelperText>
                )}

                <TextField
                  fullWidth
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your email*"
                  variant="outlined"
                  sx={inputSx}
                />
                {errors.email && (
                  <FormHelperText sx={{ color: "#ff6b6b", mb: 2 }}>
                    {errors.email}
                  </FormHelperText>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={8}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Share your story (min 20 characters)*"
                  variant="outlined"
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  sx={{
                    ...inputSx,
                    "& .MuiOutlinedInput-root textarea::placeholder": {
                      color: "#fff",
                      opacity: 0.6,
                    },
                  }}
                />
                {errors.message && (
                  <FormHelperText sx={{ color: "#ff6b6b", mb: 2 }}>
                    {errors.message}
                  </FormHelperText>
                )}

                {/* ── Inline success / error message ── */}
                {message && (
                  <Box
                    sx={{
                      mb: 2,
                      p: "12px 16px",
                      borderRadius: "6px",
                      background:
                        messageType === "success"
                          ? "rgba(46,125,50,0.15)"
                          : "rgba(198,40,40,0.15)",
                      border:
                        messageType === "success"
                          ? "1px solid rgba(46,125,50,0.4)"
                          : "1px solid rgba(198,40,40,0.4)",
                      color: messageType === "success" ? "#81c784" : "#ff8a80",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {messageType === "success" ? "✅ " : "❌ "}
                    {message}
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#C59324",
                    color: "#000",
                    py: 1.8,
                    fontWeight: 700,
                    borderRadius: "4px",
                    textTransform: "none",
                    fontSize: "1rem",
                    "&:hover": { backgroundColor: "#A67C1E" },
                  }}
                >
                  {loading ? "Submitting..." : "Send Gary Your Story"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CampaignIssues;
